import { Suspense, lazy } from 'react';
import { Route, Routes } from 'react-router-dom';
import { PageSkeleton } from '../components/PageSkeleton';
import { NotFound } from '../pages/NotFound';
import { RequireAuth } from './RequireAuth';
import { Redirect } from './Redirect';
import { RequireSection } from './RequireSection';
import { Shell } from './shell/Shell';

// Feature routes are lazy so the shell chunk stays small.
const LoginPage = lazy(() => import('../features/auth/LoginPage'));
const ResetPasswordPage = lazy(() => import('../features/auth/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('../features/auth/VerifyEmailPage'));
const HomePage = lazy(() => import('../features/home/HomePage'));
const PeopleLayout = lazy(() => import('../features/people/PeopleLayout'));
const PeoplePage = lazy(() => import('../features/people/PeoplePage'));
const OrganisationPage = lazy(() => import('../features/people/OrganisationPage'));
const QueuesPage = lazy(() => import('../features/people/QueuesPage'));
const PositionsPage = lazy(() => import('../features/people/PositionsPage'));
const RolesPage = lazy(() => import('../features/people/RolesPage'));
const RoleEditorPage = lazy(() => import('../features/people/RoleEditorPage'));
const PersonPage = lazy(() => import('../features/people/PersonPage'));
const SaturdayRotaPage = lazy(() => import('../features/people/SaturdayRotaPage'));
const LoginActivityPage = lazy(() => import('../features/people/LoginActivityPage'));
const AuditPage = lazy(() => import('../features/people/AuditPage'));
const HrLayout = lazy(() => import('../features/hr/HrLayout'));
const HrOverviewPage = lazy(() => import('../features/hr/HrOverviewPage'));
const PhonesPage = lazy(() => import('../features/operations/PhonesPage'));
const HolidayPage = lazy(() => import('../features/hr/HolidayPage'));
const RequestTab = lazy(() => import('../features/hr/RequestTab'));
const ApprovalsTab = lazy(() => import('../features/hr/ApprovalsTab'));
const CalendarPage = lazy(() => import('../features/hr/CalendarPage'));
const HolidaysAdminPage = lazy(() => import('../features/hr/admin/HolidaysAdminPage'));
const ReportsPage = lazy(() => import('../features/hr/reports/ReportsPage'));
const AbsencePage = lazy(() => import('../features/hr/absence/AbsencePage'));
const MyAbsencePage = lazy(() => import('../features/hr/absence/MyAbsencePage'));
const AbsenceReportsPage = lazy(() => import('../features/hr/absence/AbsenceReportsPage'));
const AbsenceSettingsPage = lazy(() => import('../features/hr/absence/AbsenceSettingsPage'));
const TodayPage = lazy(() => import('../features/attendance/TodayPage'));
const ExceptionsPage = lazy(() => import('../features/attendance/ExceptionsPage'));
const MyAttendancePage = lazy(() => import('../features/attendance/MyAttendancePage'));
const AttendanceSettingsPage = lazy(() => import('../features/attendance/AttendanceSettingsPage'));
const OperationsIndex = lazy(() => import('../features/attendance/OperationsIndex'));
const SalesIndex = lazy(() => import('../features/sales/SalesIndexPage'));
const SalesBranchPage = lazy(() => import('../features/sales/BranchPage'));
const SalesTargetsPage = lazy(() => import('../features/sales/TargetsPage'));
const SalesSettingsPage = lazy(() => import('../features/sales/SettingsPage'));
const SalesLeaguePage = lazy(() => import('../features/sales/LeaguePage'));
const CustomersPage = lazy(() => import('../features/customers/CustomersPage'));
const CustomerPage = lazy(() => import('../features/customers/CustomerPage'));
const ClockingHealthPage = lazy(() => import('../features/attendance/ClockingHealthPage'));
const PayrollPage = lazy(() => import('../features/hr/payroll/PayrollPage'));
// HR lifecycle (D74).
const HrMyTasksPage = lazy(() => import('../features/hr/lifecycle/MyTasksPage'));
const HrJoinersPage = lazy(() => import('../features/hr/lifecycle/JoinersPage'));
const HrHireDetailPage = lazy(() => import('../features/hr/lifecycle/HireDetailPage'));
const HrMoversPage = lazy(() => import('../features/hr/lifecycle/MoversPage'));
const HrVariationDetailPage = lazy(() => import('../features/hr/lifecycle/VariationDetailPage'));
const HrLeaversPage = lazy(() => import('../features/hr/lifecycle/LeaversPage'));
const HrLeaverDetailPage = lazy(() => import('../features/hr/lifecycle/LeaverDetailPage'));
const HrProbationPage = lazy(() => import('../features/hr/lifecycle/ProbationPage'));
const HrReviewPage = lazy(() => import('../features/hr/lifecycle/ReviewPage'));
const HrTrainingPage = lazy(() => import('../features/hr/lifecycle/TrainingPage'));
const HrRightToWorkPage = lazy(() => import('../features/hr/lifecycle/RightToWorkPage'));
const HrStatutoryPage = lazy(() => import('../features/hr/lifecycle/StatutoryPage'));
const HrCasesPage = lazy(() => import('../features/hr/lifecycle/CasesPage'));
const HrLifecycleSettingsPage = lazy(
  () => import('../features/hr/lifecycle/LifecycleSettingsPage'),
);
const DevicesPage = lazy(() => import('../features/account/DevicesPage'));
const PropertyOverviewPage = lazy(() => import('../features/property/OverviewPage'));
const PropertySitePage = lazy(() => import('../features/property/SitePage'));
const PropertyCompliancePage = lazy(() => import('../features/property/CompliancePage'));
const PropertyJobsPage = lazy(() => import('../features/property/JobsPage'));
const PropertyJobDetailPage = lazy(() => import('../features/property/JobDetailPage'));
const PropertyIssuesPage = lazy(() => import('../features/property/IssuesPage'));
const PropertySuppliersPage = lazy(() => import('../features/property/SuppliersPage'));
const PropertyLeasesPage = lazy(() => import('../features/property/LeasesPage'));
const PropertyMetersPage = lazy(() => import('../features/property/MetersPage'));
const PropertyMeterDetailPage = lazy(() =>
  import('../features/property/MetersPage').then((m) => ({ default: m.MeterDetailPage })),
);
const PropertySettingsPage = lazy(() => import('../features/property/SettingsPage'));
const FleetHomePage = lazy(() => import('../features/fleet/HomePage'));
const FleetVehiclesPage = lazy(() => import('../features/fleet/VehiclesPage'));
const FleetVehicleDetailPage = lazy(() => import('../features/fleet/VehicleDetailPage'));
const FleetDriversPage = lazy(() => import('../features/fleet/DriversPage'));
const FleetDriverDetailPage = lazy(() => import('../features/fleet/DriverDetailPage'));
const FleetGreyPage = lazy(() => import('../features/fleet/GreyFleetPage'));
const FleetSettingsPage = lazy(() => import('../features/fleet/SettingsPage'));
const MyFleetPage = lazy(() => import('../features/fleet/MyFleetPage'));
const FleetCasesPage = lazy(() => import('../features/fleet/CasesPage'));
const FleetCaseDetailPage = lazy(() => import('../features/fleet/CaseDetailPage'));
const SafetyHomePage = lazy(() => import('../features/safety/HomePage'));
const SafetyReportPage = lazy(() => import('../features/safety/ReportPage'));
const SafetyIncidentsPage = lazy(() => import('../features/safety/IncidentsPage'));
const SafetyIncidentDetailPage = lazy(() => import('../features/safety/IncidentDetailPage'));
const SafetyLoneWorkingPage = lazy(() => import('../features/safety/LoneWorkingPage'));
const SafetyCamerasPage = lazy(() => import('../features/safety/CamerasPage'));
const SafetySettingsPage = lazy(() => import('../features/safety/SettingsPage'));
const CompliancePage = lazy(() => import('../features/operations/CompliancePage'));
const ComplianceBranchPage = lazy(() => import('../features/operations/BranchPage'));
const ComplianceRegionPage = lazy(() => import('../features/operations/RegionPage'));
const ComplianceSettingsPage = lazy(() => import('../features/operations/ComplianceSettingsPage'));
const AnnouncementsAdminPage = lazy(() => import('../features/operations/AnnouncementsAdminPage'));
const AnnouncementEditorPage = lazy(() => import('../features/operations/AnnouncementEditorPage'));
const MyAnnouncementsPage = lazy(() => import('../features/operations/MyAnnouncementsPage'));
const AnnouncementPage = lazy(() => import('../features/operations/AnnouncementPage'));
const OwnCarPage = lazy(() => import('../features/account/OwnCarPage'));
const ChangePasswordPage = lazy(() => import('../features/account/ChangePasswordPage'));
const ChatPage = lazy(() => import('../features/chat/ChatPage'));
const NotificationsPage = lazy(() => import('../features/notifications/NotificationsPage'));
// Dev-only plate showcase: the import is dropped from production builds.
const DevPlatesPage = import.meta.env.DEV
  ? lazy(() => import('../features/dev/DevPlatesPage'))
  : null;

export function App() {
  return (
    // The shell has its own boundary for pages; this one catches the lazy login page.
    <Suspense fallback={<PageSkeleton />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/reset" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        {DevPlatesPage && <Route path="/dev/plates" element={<DevPlatesPage />} />}
        <Route
          element={
            <RequireAuth>
              <Shell />
            </RequireAuth>
          }
        >
          <Route index element={<HomePage />} />

          <Route path="account/devices" element={<DevicesPage />} />
          <Route path="account/password" element={<ChangePasswordPage />} />
          <Route path="account/own-car" element={<OwnCarPage />} />
          <Route path="announcements" element={<MyAnnouncementsPage />} />
          <Route path="chat" element={<ChatPage />} />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="announcements/:announcementId" element={<AnnouncementPage />} />

          <Route path="people" element={<RequireSection id="people" />}>
            <Route element={<PeopleLayout />}>
              <Route index element={<PeoplePage />} />
              <Route path="organisation" element={<OrganisationPage />} />
              <Route path="positions" element={<PositionsPage />} />
              <Route path="roles" element={<RolesPage />} />
              <Route path="roles/:code" element={<RoleEditorPage />} />
              <Route path="queues" element={<QueuesPage />} />
              <Route path="queues/:kind" element={<QueuesPage />} />
              <Route path="saturday-rota" element={<SaturdayRotaPage />} />
              <Route path="activity" element={<LoginActivityPage />} />
              <Route path="audit" element={<AuditPage />} />
            </Route>
            <Route path=":personId" element={<PersonPage />} />
          </Route>

          <Route path="hr" element={<RequireSection id="hr" />}>
            {/* "approvals" is the natural URL to guess; approvals live under Holiday. */}
            <Route path="approvals/*" element={<Redirect to="/hr/holiday/approvals/*" />} />
            <Route element={<HrLayout />}>
              <Route index element={<HrOverviewPage />} />
              <Route path="holiday" element={<HolidayPage />}>
                <Route index element={<RequestTab />} />
                <Route path="approvals" element={<ApprovalsTab />} />
                <Route path="approvals/:requestId" element={<ApprovalsTab />} />
                <Route path="approvals/absence/:absenceId" element={<ApprovalsTab />} />
              </Route>
              <Route path="calendar" element={<CalendarPage />} />
              <Route path="holidays/admin" element={<HolidaysAdminPage />} />
              <Route path="holidays/admin/:tab" element={<HolidaysAdminPage />} />
              <Route path="reports" element={<ReportsPage />} />
              <Route path="reports/:tab" element={<ReportsPage />} />
              <Route path="absence" element={<AbsencePage />} />
              <Route path="absence/me" element={<MyAbsencePage />} />
              <Route path="absence/reports" element={<AbsenceReportsPage />} />
              <Route path="absence/reports/:tab" element={<AbsenceReportsPage />} />
              <Route path="absence/settings" element={<AbsenceSettingsPage />} />
              <Route path="absence/settings/:tab" element={<AbsenceSettingsPage />} />
              <Route path="attendance" element={<TodayPage />} />
              <Route path="attendance/exceptions" element={<ExceptionsPage />} />
              <Route path="attendance/me" element={<MyAttendancePage />} />
              <Route path="attendance/settings" element={<AttendanceSettingsPage />} />
              <Route path="attendance/clocking-health" element={<ClockingHealthPage />} />
              <Route path="payroll" element={<PayrollPage />} />
              <Route path="tasks" element={<HrMyTasksPage />} />
              <Route path="joiners" element={<HrJoinersPage />} />
              <Route path="joiners/:hireId" element={<HrHireDetailPage />} />
              <Route path="movers" element={<HrMoversPage />} />
              <Route path="movers/:variationId" element={<HrVariationDetailPage />} />
              <Route path="leavers" element={<HrLeaversPage />} />
              <Route path="leavers/:leaverId" element={<HrLeaverDetailPage />} />
              <Route path="probation" element={<HrProbationPage />} />
              <Route path="probation/:reviewId" element={<HrReviewPage />} />
              <Route path="training" element={<HrTrainingPage />} />
              <Route path="right-to-work" element={<HrRightToWorkPage />} />
              <Route path="statutory" element={<HrStatutoryPage />} />
              <Route path="cases" element={<HrCasesPage />} />
              <Route path="lifecycle-settings" element={<HrLifecycleSettingsPage />} />
              <Route path="lifecycle-settings/:tab" element={<HrLifecycleSettingsPage />} />
            </Route>
          </Route>

          <Route path="sales" element={<RequireSection id="sales" />}>
            <Route index element={<SalesIndex />} />
            <Route path="branch" element={<SalesIndex />} />
            <Route path="branch/:siteId" element={<SalesBranchPage />} />
            <Route path="branch/:siteId/:tab" element={<SalesBranchPage />} />
            <Route path="region" element={<SalesLeaguePage mode="region" />} />
            <Route path="region/:regionId" element={<SalesLeaguePage mode="region" />} />
            <Route path="company" element={<SalesLeaguePage mode="company" />} />
            <Route path="targets" element={<SalesTargetsPage />} />
            <Route path="settings" element={<SalesSettingsPage />} />
          </Route>

          <Route path="operations" element={<RequireSection id="operations" />}>
            <Route index element={<OperationsIndex />} />
            {/* Attendance has one home, under HR; the old Operations URLs still land there. */}
            <Route path="today" element={<Redirect to="/hr/attendance" />} />
            <Route path="exceptions" element={<Redirect to="/hr/attendance/exceptions" />} />
            <Route path="compliance" element={<CompliancePage />} />
            <Route path="compliance/settings" element={<ComplianceSettingsPage />} />
            <Route path="compliance/branches/:siteId" element={<ComplianceBranchPage />} />
            <Route path="compliance/regions/:regionId" element={<ComplianceRegionPage />} />
            <Route path="announcements" element={<AnnouncementsAdminPage />} />
            <Route path="announcements/:announcementId" element={<AnnouncementEditorPage />} />
            <Route path="phones" element={<PhonesPage />} />
            <Route path="phones/:phoneId" element={<PhonesPage />} />
          </Route>

          <Route path="property" element={<RequireSection id="property" />}>
            <Route index element={<PropertyOverviewPage />} />
            <Route path="sites/:siteId" element={<PropertySitePage />} />
            <Route path="compliance" element={<PropertyCompliancePage />} />
            <Route path="jobs" element={<PropertyJobsPage />} />
            <Route path="jobs/:jobId" element={<PropertyJobDetailPage />} />
            <Route path="issues" element={<PropertyIssuesPage />} />
            <Route path="suppliers" element={<PropertySuppliersPage />} />
            <Route path="leases" element={<PropertyLeasesPage />} />
            <Route path="meters" element={<PropertyMetersPage />} />
            <Route path="meters/:meterId" element={<PropertyMeterDetailPage />} />
            <Route path="settings" element={<PropertySettingsPage />} />
          </Route>

          <Route path="fleet" element={<RequireSection id="fleet" />}>
            <Route index element={<FleetHomePage />} />
            <Route path="vehicles" element={<FleetVehiclesPage />} />
            <Route path="vehicles/:vehicleId" element={<FleetVehicleDetailPage />} />
            <Route path="drivers" element={<FleetDriversPage />} />
            <Route path="drivers/:personId" element={<FleetDriverDetailPage />} />
            <Route path="grey" element={<FleetGreyPage />} />
            <Route path="settings" element={<FleetSettingsPage />} />
            <Route path="me" element={<MyFleetPage />} />
            <Route path="cases" element={<FleetCasesPage />} />
            <Route path="cases/:caseId" element={<FleetCaseDetailPage />} />
          </Route>

          <Route path="safety">
            {/* Anyone can be given a corrective action: its owner opens the case (and the
                "My actions" list) without the Safety section. The API checks each case. */}
            <Route path="incidents" element={<SafetyIncidentsPage />} />
            <Route path="incidents/:incidentId" element={<SafetyIncidentDetailPage />} />
            <Route element={<RequireSection id="safety" />}>
              <Route index element={<SafetyHomePage />} />
              <Route path="report" element={<SafetyReportPage />} />
              <Route path="lone-working" element={<SafetyLoneWorkingPage />} />
              <Route path="cameras" element={<SafetyCamerasPage />} />
              <Route path="settings" element={<SafetySettingsPage />} />
            </Route>
          </Route>

          <Route path="customers" element={<RequireSection id="customers" />}>
            <Route index element={<CustomersPage />} />
            <Route path=":acct" element={<CustomerPage />} />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
